import { PDFDocument, PDFImage } from 'pdf-lib';
import { AppDispatch, RootState, store } from 'store';
import { Page } from 'store/features/pages/pagesSlice';
import { FilesDictionary } from 'FileDictionary';
import { SourcePageFullDetail } from 'store/features/newFiles/newFilesSlice';
import { ChangeEvent } from 'react';
import UTIF from 'utif';
import { resetDividedEachPageAction, addErrors } from 'store/features/context/actions';
import { addFileAction } from 'store/features/files/actions';
import { nanoid } from 'nanoid';
import { AppInsightsAsync } from './store/logger';

export function getKeyByValue(object: any, value: any) {
    return Number(Object.keys(object).find(key => object[key] === value));
}

export const getNumOfPages = async (file: Blob) => {
    const pdfDoc = await PDFDocument.load(await file.arrayBuffer());
    return pdfDoc.getPages().length;
};

export const removeFileExtension = (filename?: string) =>
    filename ? filename.substring(0, filename.lastIndexOf('.')) || filename : undefined;

export const getBlobAsync = async (pages: Page[], fileContext: RootState['files']) => {
    if (!pages.length) return;

    const files: { [id: string]: PDFDocument } = {};
    const pdfDoc = await PDFDocument.create();
    const detailsDescription: SourcePageFullDetail[] = [];

    const loadDocument = async (fileId: string): Promise<PDFDocument> => {
        if (files[fileId]) return files[fileId];
        const fileModel = fileContext.entities[fileId];
        const file = FilesDictionary[fileModel!.id!];
        const pdfFile = await PDFDocument.load(await file.arrayBuffer());
        files[fileId] = pdfFile;
        return pdfFile;
    };

    const embedImage = async (pdfDoc: PDFDocument, sourceFile: File, type: string) => {
        const arrayBuffer = await sourceFile.arrayBuffer();
        try {
            let embeddedImage: PDFImage;

            if (type === 'JPG') {
                embeddedImage = await pdfDoc.embedJpg(arrayBuffer);
            } else if (type === 'PNG') {
                embeddedImage = await pdfDoc.embedPng(arrayBuffer);
            } else {
                embeddedImage = await convertAndEmbedImage(pdfDoc, sourceFile);
            }

            const dimensions: [number, number] = [embeddedImage.width, embeddedImage.height];
            const page = pdfDoc.addPage(dimensions);
            page.drawImage(embeddedImage, {
                x: 0,
                y: 0,
                width: dimensions[0],
                height: dimensions[1]
            });
        } catch (error: any) {
            const arr = new Uint8Array(arrayBuffer).subarray(0, 128);
            const properties = {
                type,
                sourceFilename: sourceFile.name,
                sourceFileSize: sourceFile.size,
                sourceFileSignature: Array.from(arr)
                    .map(byte => byte.toString(16).padStart(2, '0'))
                    .join(''),
                message: (error as Error).message,
                errorName: (error as Error).name,
                errorCause: (error as Error).cause
            };

            console.log(properties);

            (await AppInsightsAsync)?.trackEvent({
                name: 'ADD_PAGE_ERROR',
                properties
            });

            throw error;
        }
    };

    const convertAndEmbedImage = async (pdfDoc: PDFDocument, file: File): Promise<any> => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = image.width;
                canvas.height = image.height;
                const ctx = canvas.getContext('2d')!;
                ctx.drawImage(image, 0, 0);
                canvas.toBlob(
                    async blob => {
                        if (!blob) return reject(new Error('Failed to convert image to blob'));
                        const embeddedImage = await pdfDoc.embedJpg(await blob.arrayBuffer());
                        resolve(embeddedImage);
                    },
                    'image/jpeg',
                    0.75
                );
            };
            image.onerror = async () => reject(new Error('Failed to load image'));
            image.src = URL.createObjectURL(file);
        });
    };

    try {
        for (const page of pages) {
            if (page.type === 'PDF') {
                const sourceDocument = await loadDocument(page.sourceFileId);
                const [sourcePage] = await pdfDoc.copyPages(sourceDocument, [page.pageNr - 1]);
                pdfDoc.addPage(sourcePage);
            } else {
                const sourceDocumentId = fileContext.entities[page.sourceFileId]!.id;
                const sourceFile = FilesDictionary[sourceDocumentId];
                await embedImage(pdfDoc, sourceFile, page.type);
            }

            detailsDescription.push({
                sourceFileId: fileContext.entities[page.sourceFileId]!.id,
                sourceFileRefId: fileContext.entities[page.sourceFileId]!.refId,
                sourceFilePageNr: page.pageNr,
                sourcePageId: page.id
            });
        }
    } catch (error: any) {
        console.error(error);
        (await AppInsightsAsync)?.trackException(error);
    }

    pdfDoc.setModificationDate(new Date(FilesDictionary[pages[0].sourceFileId].lastModified));
    pdfDoc.setCreationDate(new Date(FilesDictionary[pages[0].sourceFileId].lastModified));

    return {
        blob: await pdfDoc.save(),
        details: detailsDescription
    };
};

export const removeExtension = (filename: string) => filename.substring(0, filename.lastIndexOf('.')) || filename;

export type FileType = 'PDF' | 'TIF' | 'JPG' | 'PNG' | 'BMP' | 'GIF' | 'unknown';

export const checkTypeOfFile = async (file: File, filename?: string): Promise<FileType | 'unknown'> => {
    filename = filename?.toLowerCase();

    if (filename?.endsWith('.pdf')) return 'PDF';

    const allowedExtensions = /\.(png|tif|tiff|jpg|jpeg|gif|bmp)$/i;
    if (filename && !allowedExtensions.exec(filename)) {
        return 'unknown';
    }

    const arrayBuffer = await file.arrayBuffer();
    const arr = new Uint8Array(arrayBuffer).subarray(0, 4);
    const fileSignature = Array.from(arr)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');

    switch (true) {
        case fileSignature.startsWith('424d'):
            return 'BMP';
        case fileSignature.startsWith('ffd8'):
            return 'JPG';
        case fileSignature === '89504e47':
            return 'PNG';
        case fileSignature === '47494638':
            return 'GIF';
        case fileSignature === '49492a00' || fileSignature === '4d4d002a':
            return 'TIF';
        case fileSignature.startsWith('25504446'):
            return 'PDF';
        default:
            return 'unknown';
    }
};

export const addFile = async (
    file: File,
    fileId: string,
    filename: string,
    fileSize: number,
    refId?: string | number
) => {
    const type = await checkTypeOfFile(file, filename);

    if (type === 'unknown') {
        return store.dispatch(
            addErrors([
                {
                    filename,
                    error: 'Plik ma nieodpowiedni format lub jest uszkodzony. Akceptowane formaty to .pdf, .jpg, .png, .gif, .bmp, .tiff.'
                }
            ])
        );
    }

    if (type === 'TIF') {
        return handleTIFF(file, fileId, filename, fileSize, refId);
    }

    if (type === 'PDF') {
        return handlePDF(file, fileId, filename, fileSize, refId);
    }

    await addFileToStore(file, type, fileId, filename, fileSize, 1, refId);
};

export const addFilesInputHandler = async (
    FileList: FileList | null,
    dispatch: AppDispatch,
    event?: ChangeEvent<HTMLInputElement>
) => {
    if (FileList) {
        for (const file of FileList) {
            await addFile(file, nanoid(), file.name, file.size);
        }

        if (event) {
            event.target.value = '';
        }

        store.dispatch(resetDividedEachPageAction());
    }
};

const handleTIFF = async (file: File, fileId: string, filename: string, fileSize: number, refId?: string | number) => {
    const ifds = UTIF.decode(await file.arrayBuffer());

    if (ifds.length > 1) {
        return store.dispatch(
            addErrors([
                {
                    filename,
                    error: 'Akceptowane są tylko jednostronicowe pliki TIFF.'
                }
            ])
        );
    }

    UTIF.decodeImage(await file.arrayBuffer(), ifds[0]);
    const rgba = UTIF.toRGBA8(ifds[0]);

    if (!rgba) return;

    const newFile = await convertTIFFToJPEG(rgba, ifds[0].width, ifds[0].height, filename);
    await addFileToStore(newFile, 'JPG', fileId, filename, fileSize, 1, refId);
};

const convertTIFFToJPEG = (rgba: Uint8Array, width: number, height: number, filename: string): Promise<File> => {
    return new Promise(resolve => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        const imageData = ctx!.createImageData(width, height);
        imageData.data.set(rgba);
        ctx!.putImageData(imageData, 0, 0);

        canvas.toBlob(
            blob => {
                resolve(new File([blob!], filename, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.75
        );
    });
};

const handlePDF = async (file: File, fileId: string, filename: string, fileSize: number, refId?: string | number) => {
    let numOfPages = 1;

    try {
        numOfPages = await getNumOfPages(file);
    } catch (err: any) {
        const errorMessages: { [key: string]: string } = {
            header: 'Plik jest uszkodzony',
            encrypted: 'Plik jest zabezpieczony hasłem (do otwarcia lub edycji).'
        };

        const errorKey = Object.keys(errorMessages).find(key => err.message?.includes(key));
        const errorMessage = errorMessages[errorKey!] || err.message;

        return store.dispatch(addErrors([{ filename, error: errorMessage }]));
    }

    await addFileToStore(file, 'PDF', fileId, filename, fileSize, numOfPages, refId);
};

const addFileToStore = async (
    file: File,
    type: FileType,
    fileId: string,
    filename: string,
    fileSize: number,
    numOfPages: number,
    refId?: string | number
) => {
    FilesDictionary[fileId] = await cloneFileAsync(file);
    store.dispatch(addFileAction(type, fileId, filename, fileSize, numOfPages, refId));
};

async function cloneFileAsync(file: File): Promise<File> {
    const arrayBuffer = await file.arrayBuffer();
    return new File([arrayBuffer], file.name, { type: file.type, lastModified: file.lastModified });
}
